/**
 * @file     intentPersistence.ts
 * @purpose  Persist a "pending intent" across the auth bounce (sign-up / email
 *           confirmation / sign-in). Currently used for property inquiries
 *           originating from the MLS detail page CTA.
 */

export type IntentPayload = {
  kind: 'property_inquiry';
  listingKey: string;
  createdAt: number;
};

const KEY = 'relta.pendingIntent.v1';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

export function setPendingIntent(intent: IntentPayload): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(intent));
  } catch {
    // ignore quota / private-mode errors
  }
}

export function consumePendingIntent(): IntentPayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    window.localStorage.removeItem(KEY);
    const parsed = JSON.parse(raw) as IntentPayload;
    if (!parsed || parsed.kind !== 'property_inquiry') return null;
    if (typeof parsed.listingKey !== 'string' || !parsed.listingKey) return null;
    if (typeof parsed.createdAt !== 'number') return null;
    if (Date.now() - parsed.createdAt > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function peekPendingIntent(): IntentPayload | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IntentPayload;
    if (!parsed || parsed.kind !== 'property_inquiry') return null;
    return parsed;
  } catch {
    return null;
  }
}
